import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Search, FileText } from "lucide-react";
import { collection, query, orderBy, getDocs, getDoc, doc } from "firebase/firestore";
import { db } from "@/lib/firebase";
 
interface DealDocumentRow {
  id: string; deal_id: string; document_type: string; document_reference: string | null;
  status: string; created_at: string; signed_at: string | null; business_name?: string; deal_reference?: string;
}
 
const STATUS_COLORS: Record<string, string> = {
  draft: "bg-muted text-muted-foreground",
  pending_signature: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30",
  partially_signed: "bg-orange-500/20 text-orange-300 border-orange-500/30",
  signed: "bg-green-500/20 text-green-300 border-green-500/30",
  expired: "bg-red-500/20 text-red-300 border-red-500/30",
  rejected: "bg-destructive/20 text-destructive border-destructive/30",
};
 
const TYPE_LABELS: Record<string, string> = { nda: "NDA", loi: "LOI", cim: "CIM", contract: "Contract", offer: "Lender Offer", other: "Other" };
 
export function DealDocumentsTable() {
  const [documents, setDocuments] = useState<DealDocumentRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
 
  useEffect(() => {
    async function fetchDocuments() {
      try {
        const snap = await getDocs(query(collection(db, "dealDocuments"), orderBy("createdAt", "desc")));
        const rows: DealDocumentRow[] = await Promise.all(snap.docs.map(async d => {
          const data = d.data();
          let business_name: string | undefined;
          let deal_reference: string | undefined;
          if (data.dealId) {
            try {
              const dealSnap = await getDoc(doc(db, "deals", data.dealId));
              if (dealSnap.exists()) {
                business_name = dealSnap.data().businessName || dealSnap.data().business_name;
                deal_reference = dealSnap.data().referenceNumber;
              }
            } catch {}
          }
          return {
            id: d.id, deal_id: data.dealId || data.deal_id || "",
            document_type: data.documentType || data.document_type || "",
            document_reference: data.documentReference || data.document_reference || null,
            status: data.status || "draft",
            created_at: data.createdAt?.toDate?.()?.toISOString() || new Date().toISOString(),
            signed_at: data.signedAt?.toDate?.()?.toISOString() || null,
            business_name, deal_reference,
          };
        }));
        setDocuments(rows);
      } catch (error) {
        console.error("Error fetching documents:", error);
      } finally { setLoading(false); }
    }
    fetchDocuments();
  }, []);
 
  const filtered = documents.filter(doc => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (doc.document_reference?.toLowerCase().includes(q)) || (doc.business_name?.toLowerCase().includes(q)) || (doc.deal_reference?.toLowerCase().includes(q)) || doc.document_type.toLowerCase().includes(q);
  });
 
  return (
    <Card className="bg-white/10 border-white/20">
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle className="text-white flex items-center gap-2"><FileText className="h-5 w-5" />Documents ({documents.length})</CardTitle>
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-white/40" />
          <Input placeholder="Search by reference..." value={search} onChange={e => setSearch(e.target.value)} className="pl-10 bg-white/10 border-white/20 text-white placeholder:text-white/40 h-9" />
        </div>
      </CardHeader>
      <CardContent className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead><tr className="border-b border-white/10">
              {["Doc Reference","Type","Deal","Status","Created","Signed"].map(h => <th key={h} className="text-left px-4 py-3 text-sm font-medium text-white/60">{h}</th>)}
            </tr></thead>
            <tbody>
              {loading ? <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">Loading documents...</td></tr>
              : filtered.length === 0 ? <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No documents found</td></tr>
              : filtered.map(doc => (
                <tr key={doc.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                  <td className="px-4 py-3"><span className="text-xs font-mono text-brand-gold font-semibold">{doc.document_reference || "—"}</span></td>
                  <td className="px-4 py-3"><Badge variant="outline" className="text-white border-white/20">{TYPE_LABELS[doc.document_type] || doc.document_type}</Badge></td>
                  <td className="px-4 py-3"><div><p className="text-sm text-white">{doc.business_name || "—"}</p><p className="text-xs text-white/40 font-mono">{doc.deal_reference || ""}</p></div></td>
                  <td className="px-4 py-3"><Badge variant="outline" className={STATUS_COLORS[doc.status] || "text-white"}>{doc.status.replace(/_/g," ")}</Badge></td>
                  <td className="px-4 py-3 text-white/60 text-sm">{new Date(doc.created_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3 text-white/60 text-sm">{doc.signed_at ? new Date(doc.signed_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}
 